import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setShowedFilters } from '../redux/slices/ganttSlice';
import * as ganttActions from '../redux/slices/ganttSlice';
import { defaultViewService } from '../services/defaultViews.service';
import { cleanColumnsIssues } from '../views/ganttContainer/gantt/viewUtils';
import {
  setGeneralFilterWithoutGanttUpdate,
  setRangeFilterWithoutGanttUpdate,
  setScaleVisualizaTionFilter,
  setVisualizationAndColorsFilter,
  setOrderFilter,
  setCollapsedFilter
} from '../views/ganttContainer/gantt/defaultView/filters';
import { sentryLogger } from '../monitor/monitor';

/**
 * Custom hook to handle loading and applying custom views in the Gantt
 * @returns {Object} Object with functions and state to handle views
 */
export const useGanttCustomViews = () => {
  const dispatch = useDispatch();
  const [isSettingView, setIsSettingView] = useState(false);

  /**
   * Loads and applies the default view
   * @param {Object} gantt - Gantt instance
   * @param {number|string} sectorId - Sector ID
   */
  const loadAndApplyDefaultView = async (gantt, sectorId) => {
    if (!gantt || !sectorId) {
      return;
    }

    try {
      setIsSettingView(true);

      // 1. Load default view
      const defaultView = await defaultViewService.getDefaultView(sectorId);

      let defaultViewTemplate = null;

      // 2. Process view
      if (Array.isArray(defaultView)) {
        dispatch(ganttActions.setViews({}));
      } else {
        if (
          !defaultView ||
          !defaultView.defaultView ||
          !defaultView.defaultView.id
        ) {
          dispatch(ganttActions.setViews({}));
          return;
        }

        const DEFAULT_VIEW_ID = defaultView.defaultView.id;

        if (
          !defaultView.scheduleViews ||
          !Array.isArray(defaultView.scheduleViews)
        ) {
          dispatch(ganttActions.setViews({}));
          return;
        }

        defaultViewTemplate = defaultView.scheduleViews.find(
          (view) => view.id === DEFAULT_VIEW_ID
        );

        if (defaultViewTemplate) {
          defaultViewTemplate.columns = cleanColumnsIssues(
            defaultViewTemplate.columns
          );

          dispatch(
            ganttActions.setViews({
              ...defaultViewTemplate
            })
          );
        } else {
          dispatch(ganttActions.setViews({}));
        }
      }

      // 3. Apply view
      if (defaultViewTemplate) {
        applyTemplateFilters(gantt, defaultViewTemplate);
      } else {
        applyDefaultConfigurations(gantt);
      }
    } catch (error) {
      sentryLogger.error('Error loading and applying default view', error);
      dispatch(ganttActions.setViews({}));
    } finally {
      setIsSettingView(false);
    }
  };

  /**
   * Checks if the view template is empty
   * @param {Object} template - View template
   * @returns {boolean} true if empty, false if not
   * @private
   */
  const isDefaultViewTemplateEmpty = (template) =>
    !template || Object.keys(template).length === 0;

  /**
   * Applies template filters
   * @param {Object} gantt - Gantt instance
   * @param {Object} template - View template
   * @private
   */
  const applyTemplateFilters = (gantt, template) => {
    if (!gantt || !template || isDefaultViewTemplateEmpty(template)) return;

    try {
      // Apply general filters
      if (template.filters) {
        applyGeneralFilters(template, gantt);
      }

      // Apply range filters
      if (template.range) {
        applyRangeFilters(template, gantt);
      }

      // Apply column filters
      if (template.columns) {
        setColumnsFilter(template.columns, gantt, template.columns);
      }

      // Apply other filters
      applyOtherFilters(template, gantt);
    } catch (error) {
      sentryLogger.error('Error applying template filters:', error);
    }
  };

  /**
   * Applies general filters
   * @param {Object} template - View template
   * @param {Object} gantt - Gantt instance
   * @private
   */
  const applyGeneralFilters = (template, gantt) => {
    setGeneralFilterWithoutGanttUpdate(template.filters, gantt, false).then(
      () => {
        dispatch(setShowedFilters(template.filters));
      }
    );
  };

  /**
   * Applies range filters
   * @param {Object} template - View template
   * @param {Object} gantt - Gantt instance
   * @private
   */
  const applyRangeFilters = (template, gantt) => {
    setRangeFilterWithoutGanttUpdate(template.range, gantt, (dateStrings) => {
      dispatch(
        ganttActions.setIsRangeFiltered({
          boolean: true,
          range: dateStrings
        })
      );
    });
  };

  /**
   * Applies other filters (zoom, visualization, order, collapsed)
   * @param {Object} template - View template
   * @param {Object} gantt - Gantt instance
   * @private
   */
  const applyOtherFilters = (template, gantt) => {
    if (template.zoom) {
      setScaleVisualizaTionFilter(template.zoom, gantt);
    }

    if (template.visualization && template.colors) {
      setVisualizationAndColorsFilter(
        template.visualization,
        template.colors,
        gantt
      );
    }

    if (template.order) {
      setOrderFilter(template.order, gantt);
    }

    if (template.collapsed) {
      setCollapsedFilter(template.collapsed, gantt, true);
    }
  };

  /**
   * Applies default configurations when no view template exists
   * @param {Object} gantt - Gantt instance
   * @private
   */
  const applyDefaultConfigurations = (gantt) => {
    if (!gantt) return;

    try {
      const defaultZoom = {
        type: 'MONTH',
        code: 0
      };

      setScaleVisualizaTionFilter(defaultZoom, gantt);
    } catch (error) {
      sentryLogger.error('Error applying default configurations', error);
    }
  };

  /**
   * Orders columns by position
   * @param {Array} columns - Array of columns to sort
   * @returns {Map} Map of columns ordered by position
   */
  const orderColumnsByPosition = (columns) => {
    const sortedColumns = columns.sort((columnA, columnB) => {
      if (columnA.position < columnB.position) return -1;
      if (columnA.position > columnB.position) return 1;
      return 0;
    });

    const mapOfColumnsOrderedByPosition = new Map();
    sortedColumns.forEach((column) => {
      mapOfColumnsOrderedByPosition.set(column.name, column);
    });

    return mapOfColumnsOrderedByPosition;
  };

  /**
   * Combines original columns with the specified order
   * @param {Map} mapOfOrderedColumns - Map of ordered columns
   * @param {Object} gantt - Gantt instance
   * @returns {Array} Array of combined and ordered columns
   */
  const combineColumns = (mapOfOrderedColumns, gantt) => {
    const originalColumns = gantt.config.columns.slice();
    const RANDOM_NUMBER_TO_ALLOW_SORT = 1000;

    const getPosition = (element) =>
      mapOfOrderedColumns.has(element.name)
        ? mapOfOrderedColumns.get(element.name)
        : {
            position: RANDOM_NUMBER_TO_ALLOW_SORT
          };

    originalColumns.sort(
      (columnA, columnB) =>
        getPosition(columnA).position - getPosition(columnB).position
    );

    return originalColumns;
  };

  /**
   * Sets column widths
   * @param {Array} columns - Array of columns
   * @param {Object} gantt - Gantt instance
   */
  const setColumnsWidth = (columns, gantt) => {
    columns.forEach((column) => {
      const gridColumn = gantt.getGridColumn(column.name);
      if (gridColumn) {
        gridColumn.width = column.width;
      }
    });
  };

  /**
   * Sets column visibility
   * @param {Array} columns - Array of columns
   * @param {Object} gantt - Gantt instance
   */
  const setColumnsVisibility = (columns, gantt) => {
    columns.forEach((column) => {
      const gridColumn = gantt.getGridColumn(column.name);
      if (gridColumn) {
        gridColumn.hide = column.hide;
      }
    });
  };

  /**
   * Applies column filters
   * @param {Array} columns - Array of columns
   * @param {Object} gantt - Gantt instance
   * @param {Array} activatedColumns - Array of activated columns
   */
  const setColumnsFilter = (columns = [], gantt, activatedColumns) => {
    if (!columns.length) return;

    const visibleColumns = columns.filter((column) => !column.hide);
    const visibleColumnsValues = visibleColumns.map((column) => column.value);

    if (visibleColumnsValues.length < activatedColumns.length) {
      const colsToDisable = activatedColumns.filter(
        (x) => !visibleColumnsValues.includes(x)
      );
      colsToDisable.forEach((colToDisable) => {
        if (gantt.getGridColumn(colToDisable)) {
          gantt.getGridColumn(colToDisable).hide = true;
        }
      });
    }

    visibleColumnsValues.forEach((columnName) => {
      if (gantt.getGridColumn(columnName)) {
        gantt.getGridColumn(columnName).hide = false;
      }
    });

    dispatch(ganttActions.setActivatedColumns(visibleColumnsValues));
    const mapOfOrderedColumns = orderColumnsByPosition(columns);
    const newColumnsOrder = combineColumns(mapOfOrderedColumns, gantt);
    gantt.config.columns = newColumnsOrder;
    setColumnsWidth(columns, gantt);
    setColumnsVisibility(columns, gantt);
  };

  return {
    loadAndApplyDefaultView,
    isSettingView
  };
};

export default useGanttCustomViews;
